const BASE_URL = "https://prompts.alphatorana.com/api/otp";

/**
 * Decode JWT payload (base64url) without verification.
 * Use only for non-sensitive UI hints before auth is completed.
 */
export function decodeJwtPayload(token) {
  try {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Call POST /api/otp/generate
 * Returns { success, message, token, expiresIn }
 */
export async function generateOTP(email) {
  const res = await fetch(`${BASE_URL}/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  const data = await res.json();

  if (!res.ok || !data.success) {
    throw new Error(data.message || "Failed to send OTP. Please try again.");
  }

  return data; // { success, message, token, expiresIn }
}

/**
 * Call POST /api/otp/validate
 * Returns { success, message }
 */
export async function validateOTP(token, otp) {
  const res = await fetch(`${BASE_URL}/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, otp }),
  });

  const data = await res.json();

  if (!res.ok || !data.success) {
    throw new Error(data.message || "Invalid OTP. Please try again.");
  }

  return data; // { success, message }
}

// ── Encryption helpers (AES-GCM via Web Crypto API) ─────────────
// The cookie value is AES-256-GCM encrypted so it's opaque to
// anyone inspecting cookies. Tampering with the value causes
// decryption to fail and the session is treated as invalid.

const APP_SECRET = "AT@FNOL#2026$SecureKey!XyZ9qRsT"; // 32-char key material

async function getKey() {
  const enc = new TextEncoder();
  const raw = enc.encode(APP_SECRET);
  const keyMaterial = await crypto.subtle.importKey(
    "raw", raw, { name: "PBKDF2" }, false, ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode("at-fnol-salt-v1"),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function encrypt(plaintext) {
  const key = await getKey();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
  const enc = new TextEncoder();
  const cipherBuf = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(plaintext)
  );
  // Concatenate iv (12 bytes) + ciphertext, then base64url-encode
  const combined = new Uint8Array(iv.byteLength + cipherBuf.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(cipherBuf), iv.byteLength);
  return btoa(String.fromCharCode(...combined))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

async function decrypt(encoded) {
  try {
    const key = await getKey();
    // Re-pad and decode base64url
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const iv = bytes.slice(0, 12);
    const cipherBuf = bytes.slice(12);
    const plainBuf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      cipherBuf
    );
    return new TextDecoder().decode(plainBuf);
  } catch {
    return null; // tampered or invalid cookie
  }
}

// ── Cookie helpers ───────────────────────────────────────────────

function setCookie(name, value, hours) {
  const expires = new Date(Date.now() + hours * 60 * 60 * 1000).toUTCString();
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict${secure}`;
}

function getCookieRaw(name) {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? match.split("=")[1] : null;
}

function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict`;
}

// ── Session API ──────────────────────────────────────────────────

const SESSION_HOURS = 8;
const SESSION_COOKIE = "at_sess";

export async function setSession(email, name = "") {
  const now = Date.now();
  const payload = JSON.stringify({
    email,
    name,
    ts: now,
    expiresAt: now + SESSION_HOURS * 60 * 60 * 1000,
  });
  const encrypted = await encrypt(payload);
  setCookie(SESSION_COOKIE, encrypted, SESSION_HOURS);
}

export function clearSession() {
  deleteCookie(SESSION_COOKIE);
}

async function getSessionPayload() {
  const raw = getCookieRaw(SESSION_COOKIE);
  if (!raw) return null;
  const plaintext = await decrypt(raw);
  if (!plaintext) return null;
  try {
    return JSON.parse(plaintext);
  } catch {
    return null;
  }
}

export async function isAuthenticated() {
  const payload = await getSessionPayload();
  if (!payload) return false;
  // Check embedded expiry (belt-and-suspenders on top of cookie expiry)
  if (Date.now() > payload.expiresAt) {
    clearSession();
    return false;
  }
  return true;
}

export async function getSessionEmail() {
  const payload = await getSessionPayload();
  return payload?.email || "";
}

export async function getSessionName() {
  const payload = await getSessionPayload();
  return payload?.name || "";
}
